import { doc, serverTimestamp, writeBatch } from '@firebase/firestore';
import { db } from '../../firebase-config';
import { CHECK_UPS_REQUIRED, FORM_STATUS } from '../../utils/constants';
import { CHECKUP_STATUS } from './constants';

const { IN_PROGRESS, AWAITING_PAYMENT, AWAITING_REVIEW } = CHECKUP_STATUS;

/**
 *
 * @param {object} props
 * @param {object} props.userData - The user data object containing checkups and checkupsRequired.
 * @returns {string[]} - A promise that resolves to an array of checkup IDs that are pending.
 */
export const getPendingCheckups = ({ userData }) => {
  const checkups = userData?.checkups || {};

  /** @type {string[]} */
  const checkupsRequired = userData?.[CHECK_UPS_REQUIRED] || [];

  if (checkupsRequired.length > 0) {
    return checkupsRequired;
  }

  return Object.keys(checkups).filter((checkup) => {
    const checkupData = checkups[checkup];

    return (
      checkupData?.status === IN_PROGRESS ||
      checkupData?.status === AWAITING_PAYMENT ||
      checkupData?.status === AWAITING_REVIEW
    );
  });
};

/**
 *
 * @param {Partial<GeneralPatientInfo>} userData - The user data object containing checkups & prescriptions.
 * @returns {string[]} - An array of available checkups from userData.checkups
 */
export const getAvailableCheckups = (userData) => {
  const checkups = userData?.checkups || {};
  const patientPrescriptions = userData?.prescriptions || {};

  const treatmentsWithPrescriptions = Object.entries(patientPrescriptions)
    .filter(([_, items]) => Object.keys(items).length > 0)
    .map(([treatment]) => treatment);

  if (Object.keys(checkups).length === 0 && treatmentsWithPrescriptions.length === 0) {
    return [];
  }

  if (treatmentsWithPrescriptions.length > 0) {
    return treatmentsWithPrescriptions;
  }

  return Object.keys(checkups).filter((checkup) => {
    const checkupData = checkups[checkup];

    return checkupData?.status !== AWAITING_PAYMENT && checkupData?.status !== AWAITING_REVIEW;
  });
};

/**
 *
 * @param {object} props
 * @param {number} props.nowInMillis - The current time in milliseconds.
 * @param {string[]} props.healthCategories - The health categories to update.
 * @param {CheckupStatus} props.checkupStatus - The status to set for the checkups.
 * @param {boolean} [props.resetFormsCheckup] - Whether to reset the forms checkup status.
 * @param {string} [props.paymentReference] - The payment reference, if applicable.
 */
export const buildCheckupUpdates = ({
  nowInMillis,
  healthCategories,
  checkupStatus,
  resetFormsCheckup,
  paymentReference,
}) => ({
  patientDocUpdate: healthCategories.reduce(
    (acc, category) => ({
      ...acc,
      [`checkups.${category}`]: checkupStatus,
      ...(resetFormsCheckup && {
        [`forms.checkup`]: FORM_STATUS.IN_PROGRESS,
      }),
    }),
    {},
  ),

  generalInfoDocUpdate: healthCategories.reduce(
    (acc, category) => ({
      ...acc,
      [`checkups.${category}.updatedAt`]: nowInMillis,
      [`checkups.${category}.status`]: checkupStatus,
      [`checkups.${category}.serverUpdatedAt`]: serverTimestamp(),
      ...(paymentReference !== undefined && {
        [`checkups.${category}.lastPaymentReference`]: paymentReference,
      }),
      ...(resetFormsCheckup && {
        [`forms.checkup.step`]: 0,
      }),
    }),
    {},
  ),
});

/**
 * Updates the checkup statuses for a patient in the database.
 * @param {Object} props - The parameters for the update.
 * @param {number} props.nowInMillis - The current time in milliseconds.
 * @param {string} props.patientId - The ID of the user whose checkup statuses are being updated.
 * @param {string[]} props.healthCategories - The health categories to update.
 * @param {CheckupStatus} props.checkupStatus - The status to set for the checkups.
 * @param {boolean} [props.resetFormsCheckup] - Whether to reset the forms checkup status.
 * @param {string} [props.paymentReference] - The payment reference, if applicable.
 */
export const updateCheckupStatuses = async ({
  nowInMillis,
  patientId,
  healthCategories,
  checkupStatus,
  paymentReference,
}) => {
  const patientDocRef = doc(db, 'patients', patientId);
  const patientGeneralInfoDocRef = doc(db, 'patients', patientId, 'general', 'information');

  const { patientDocUpdate, generalInfoDocUpdate } = buildCheckupUpdates({
    nowInMillis,
    healthCategories,
    paymentReference,
    checkupStatus,
  });

  const batch = writeBatch(db);
  batch.update(patientDocRef, patientDocUpdate);
  batch.update(patientGeneralInfoDocRef, generalInfoDocUpdate);
  await batch.commit();
};
